import React, { useState, useEffect, useRef } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Development {
  id: string;
  title: string;
  deal_value: string | null;
  units: string | null;
  yield_pct: string | null;
  stage: string | null;
  phase: string | null;
  proclamation: string | null;
  rag: string | null;
  end_date: string | null;
  permalink: string | null;
}

interface PrincipalsPayload {
  developments: Development[];
  fetched_at: number;
}

// ── Password gate ─────────────────────────────────────────────────────────────

const PASSWORD = 'EmpiriQ2026';

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value === PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 1200);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: '#171717' }}
    >
      <div
        className="w-full max-w-sm mx-4 rounded-2xl border border-white/10 p-8 flex flex-col items-center gap-6"
        style={{ background: 'rgba(255,255,255,0.04)' }}
      >
        {/* Logo mark */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-4xl font-black tracking-tight" style={{ color: '#60CAF3' }}>
            EmpiriQ
          </span>
          <span className="text-xs uppercase tracking-widest text-white/40">
            Principals View
          </span>
        </div>

        <form onSubmit={submit} className="w-full flex flex-col gap-3">
          <input
            ref={inputRef}
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Access code"
            className={`w-full rounded-xl px-4 py-3 text-sm bg-white/10 border text-white placeholder-white/30 outline-none focus:ring-2 transition-all ${
              error
                ? 'border-red-500 ring-red-500/30'
                : 'border-white/20 focus:border-[#60CAF3] focus:ring-[#60CAF3]/20'
            }`}
            autoComplete="off"
            spellCheck={false}
          />
          {error && (
            <p className="text-xs text-red-400 text-center animate-pulse">Incorrect code</p>
          )}
          <button
            type="submit"
            className="w-full rounded-xl py-3 text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ background: '#60CAF3', color: '#171717' }}
          >
            Enter
          </button>
        </form>
      </div>
    </div>
  );
}

// ── RAG dot ───────────────────────────────────────────────────────────────────

function RagDot({ rag }: { rag: string | null }) {
  const colours: Record<string, string> = {
    Green: '#22c55e', green: '#22c55e', G: '#22c55e',
    Amber: '#f59e0b', amber: '#f59e0b', A: '#f59e0b',
    Red:   '#ef4444', red:   '#ef4444', R: '#ef4444',
  };
  const colour = rag ? (colours[rag] ?? '#94a3b8') : '#94a3b8';
  return (
    <span
      title={rag ?? '—'}
      style={{ backgroundColor: colour, display: 'inline-block', width: 12, height: 12, borderRadius: '50%' }}
    />
  );
}

// ── Steerco table ─────────────────────────────────────────────────────────────

function SteercoTable({ developments }: { developments: Development[] }) {
  const fmt = (date: string | null) =>
    date
      ? new Date(date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-sm border-collapse"
        style={{ minWidth: 760 }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
            {[
              'Project',
              'Deal Value',
              'Units',
              'Yield %',
              'Stage',
              'Proclamation',
              'RAG',
              'Next Milestone',
            ].map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                style={{ color: '#60CAF3', whiteSpace: 'nowrap' }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {developments.map((d, i) => (
            <tr
              key={d.id}
              style={{
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
              }}
              className="hover:bg-white/5 transition-colors"
            >
              <td className="px-4 py-3 font-medium text-white whitespace-nowrap">
                {d.title}
              </td>
              <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                {d.deal_value ? `R ${Number(d.deal_value).toLocaleString('en-ZA')}` : '—'}
              </td>
              <td className="px-4 py-3 text-white/70 text-center">{d.units ?? '—'}</td>
              <td className="px-4 py-3 text-white/70 text-center">
                {d.yield_pct ? `${d.yield_pct}%` : '—'}
              </td>
              <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                {d.stage ?? d.phase ?? '—'}
              </td>
              <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                {d.proclamation ?? '—'}
              </td>
              <td className="px-4 py-3 text-center">
                <RagDot rag={d.rag} />
              </td>
              <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmt(d.end_date)}</td>
            </tr>
          ))}
          {developments.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-8 text-center text-white/30 text-xs italic">
                No developments found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Principals() {
  const [unlocked, setUnlocked] = useState(false);
  const [payload, setPayload] = useState<PrincipalsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/wrike/principals');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: PrincipalsPayload = await res.json();
      setPayload(data);
      setLastFetch(new Date().toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = () => {
    setUnlocked(true);
    load();
  };

  if (!unlocked) return <PasswordGate onUnlock={handleUnlock} />;

  return (
    <div
      className="min-h-screen"
      style={{ background: '#171717', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      {/* Header */}
      <header
        className="flex items-center justify-between px-6 py-5 border-b border-white/10 print:border-none"
        style={{ background: '#171717' }}
      >
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#60CAF3' }}>EmpiriQ</h1>
          <p className="text-xs text-white/40 mt-0.5 uppercase tracking-widest">
            Principals / Steerco Report
          </p>
        </div>
        <div className="flex items-center gap-3 print:hidden">
          {lastFetch && (
            <span className="text-xs text-white/30 hidden sm:inline">Updated {lastFetch}</span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-opacity disabled:opacity-40"
            style={{ background: '#60CAF3', color: '#171717' }}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={() => window.print()}
            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-white/20 text-white/70 hover:text-white transition-colors"
          >
            Print
          </button>
          <a
            href="/dashboard"
            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-white/20 text-white/70 hover:text-white transition-colors"
          >
            ← HQ
          </a>
        </div>
      </header>

      {/* Print header — only visible when printing */}
      <div className="hidden print:block px-6 py-4">
        <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>
          EmpiriQ Holdings — Developments Register
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Printed {new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      <main className="px-4 py-6 md:px-8 max-w-screen-xl mx-auto">
        {/* Loading */}
        {loading && !payload && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div
                className="h-8 w-8 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: '#60CAF3', borderTopColor: 'transparent' }}
              />
              <span className="text-white/40 text-sm">Loading EmpiriQ data…</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-5 text-red-300 text-sm">
            Failed to load: {error}
            <button onClick={load} className="ml-4 underline opacity-70 hover:opacity-100">
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        {payload && !loading && (
          <>
            {/* Summary strip */}
            <div className="flex flex-wrap gap-6 mb-6 print:mb-4">
              <div>
                <p className="text-xs text-white/40 uppercase tracking-wider">Developments</p>
                <p className="text-2xl font-bold text-white">{payload.developments.length}</p>
              </div>
              {(['Green', 'Amber', 'Red'] as const).map((colour) => {
                const n = payload.developments.filter(
                  (d) => d.rag === colour || d.rag === colour[0],
                ).length;
                const dotColour = { Green: '#22c55e', Amber: '#f59e0b', Red: '#ef4444' }[colour];
                return (
                  <div key={colour}>
                    <p className="text-xs text-white/40 uppercase tracking-wider flex items-center gap-1">
                      <span
                        style={{ background: dotColour, display: 'inline-block', width: 8, height: 8, borderRadius: '50%' }}
                      />
                      {colour}
                    </p>
                    <p className="text-2xl font-bold text-white">{n}</p>
                  </div>
                );
              })}
            </div>

            <div
              className="rounded-xl border border-white/10 overflow-hidden print:border-gray-300"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <SteercoTable developments={payload.developments} />
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-8 text-center text-xs text-white/20 print:hidden">
        EmpiriQ Holdings · Confidential
      </footer>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: #fff !important; color: #000 !important; }
          table { color: #000 !important; }
          th { color: #374151 !important; }
          td { color: #111827 !important; }
          tr { border-bottom: 1px solid #e5e7eb !important; background: transparent !important; }
          .print\\:hidden { display: none !important; }
          .print\\:border-none { border: none !important; }
          .print\\:block { display: block !important; }
          .print\\:mb-4 { margin-bottom: 1rem !important; }
        }
      `}</style>
    </div>
  );
}
